import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { get } from '../../utils/apiClient';
import { doseSpotErrorMessage } from './doseSpotErrorMessage';

// The hosted app's panes squash and spawn nested scrollbars when the iframe
// viewport is small, so the frame renders at 1/SCALE of the dialog size and is
// scaled down to fit — DoseSpot lays out against a larger viewport while still
// filling the dialog visually. Its document owns the single scrollbar.
const IFRAME_SCALE = 0.8;

interface DoseSpotDialogProps {
  clientId?: string;
  open: boolean;
  onClose: () => void;
  notificationSummary?: boolean;
  title?: string;
}

export async function openDoseSpotWindow(
  clientId?: string,
  notificationSummary = false,
): Promise<void> {
  const endpoint = notificationSummary
    ? '/erx/notification-summary-url'
    : `/erx/sso-url/${clientId}`;
  const tab = window.open('about:blank', '_blank');
  try {
    const res = await get(endpoint);
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new Error(doseSpotErrorMessage(data, 'Failed to load prescribing interface'));
    }
    const data = await res.json();
    if (!data.url) {
      throw new Error('DoseSpot is not configured yet. Contact your administrator to add API credentials.');
    }
    if (tab) {
      tab.location.href = data.url;
    } else {
      window.open(data.url, '_blank', 'noopener,noreferrer');
    }
  } catch (error) {
    tab?.close();
    throw error;
  }
}

export default function DoseSpotDialog({
  clientId,
  open,
  onClose,
  notificationSummary = false,
  title,
}: DoseSpotDialogProps) {
  const [ssoUrl, setSsoUrl] = useState('');
  const [iframeVersion, setIframeVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openingDirect, setOpeningDirect] = useState(false);
  const [directOpenError, setDirectOpenError] = useState('');
  const [reopeningSummary, setReopeningSummary] = useState(false);
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const isMobile = useIsMobile();
  const touchStartY = useRef<number | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // DoseSpot SSO URLs are single-use: every consumer must fetch its own URL.
  const fetchSsoUrl = useCallback(async (): Promise<string> => {
    const endpoint = notificationSummary
      ? '/erx/notification-summary-url'
      : `/erx/sso-url/${clientId}`;
    const res = await get(endpoint);
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new Error(doseSpotErrorMessage(data, 'Failed to load prescribing interface'));
    }
    const data = await res.json();
    if (!data.url) {
      throw new Error('DoseSpot is not configured yet. Contact your administrator to add API credentials.');
    }
    return data.url;
  }, [clientId, notificationSummary]);

  useEffect(() => {
    if (!open || (!clientId && !notificationSummary)) return;
    let cancelled = false;
    const fetchUrl = async () => {
      setLoading(true);
      setError('');
      try {
        const url = await fetchSsoUrl();
        if (!cancelled) setSsoUrl(url);
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load prescribing interface');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUrl();
    return () => { cancelled = true; };
  }, [open, clientId, notificationSummary, fetchSsoUrl]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    touchStartY.current = null;
    e.stopPropagation();
  }, []);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const handleOpenDoseSpot = useCallback(async () => {
    setDirectOpenError('');
    setOpeningDirect(true);
    try {
      await openDoseSpotWindow(clientId, notificationSummary);
    } catch (err) {
      setDirectOpenError(err instanceof Error ? err.message : 'Unable to open DoseSpot');
    } finally {
      setOpeningDirect(false);
    }
  }, [clientId, notificationSummary]);

  const handleOpenDoseSpotEntrypoint = useCallback(async () => {
    setReopeningSummary(true);
    setError('');
    try {
      const url = await fetchSsoUrl();
      setSsoUrl(url);
      setIframeVersion((version) => version + 1);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reload DoseSpot');
    } finally {
      setReopeningSummary(false);
    }
  }, [fetchSsoUrl]);

  if (!open) return null;

  const containerClass = isMobile
    ? 'fixed inset-0 z-50 flex flex-col bg-white dark:bg-gray-800'
    : 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50';

  // The hosted DoseSpot app lays out its own panes against the iframe
  // viewport, so the dialog needs most of the window — while leaving a
  // visible margin and the Kindredly header so it still reads as in-app.
  const panelClass = isMobile
    ? 'flex flex-col h-full w-full'
    : 'bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[94vw] h-[92vh] flex flex-col';

  return (
    <div
      className={containerClass}
      onClick={isMobile ? undefined : handleBackdropClick}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={dialogRef}
        className={panelClass}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b dark:border-gray-700 shrink-0">
          <div className="flex flex-wrap items-center gap-3">
            {!loading && !error && (
              <button
                type="button"
                onClick={() => void handleOpenDoseSpotEntrypoint()}
                disabled={reopeningSummary}
                className="min-h-[44px] rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                {reopeningSummary ? 'Refreshing…' : 'Refresh'}
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title || (notificationSummary ? 'DoseSpot Notifications' : 'Prescribe Medication')}
            </h2>
            {!noticeDismissed && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <p>
                  For a larger view, or if DoseSpot is blocked by browser or vendor framing policy, use Open in new window.
                </p>
                <button
                  type="button"
                  onClick={() => setNoticeDismissed(true)}
                  aria-label="Dismiss"
                  className="shrink-0 px-2 py-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  &times;
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!loading && !error && ssoUrl && (
              <button
                type="button"
                onClick={() => void handleOpenDoseSpot()}
                disabled={openingDirect}
                className="min-h-[44px] shrink-0 rounded border border-indigo-600 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
              >
                {openingDirect ? 'Opening…' : 'Open in new window'}
              </button>
            )}
            <button
              onClick={onClose}
              className="flex items-center justify-center min-w-[44px] min-h-[44px] text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-xl leading-none rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-2">{error}</p>
              <p className="text-sm text-gray-500">Contact your administrator to configure DoseSpot credentials.</p>
            </div>
          ) : (
            <>
              {directOpenError && (
                <p className="mb-3 text-sm text-red-600" role="alert">{directOpenError}</p>
              )}
              {isMobile ? (
                <div className="flex flex-1 items-start justify-center overflow-y-auto py-8 text-center">
                  <p className="max-w-sm text-sm text-gray-600 dark:text-gray-300">
                    DoseSpot opens in a separate window on mobile for a larger, more reliable prescribing workspace.
                  </p>
                </div>
              ) : (
                <div
                  className="flex-1 min-h-0 overflow-hidden overscroll-contain"
                  role="region"
                  aria-label="DoseSpot content"
                >
                  {/* The oversized frame is scaled back to the dialog size, so
                      the wrapper clips its layout overhang and the hosted
                      document keeps the only scrollbar. */}
                  <iframe
                    key={iframeVersion}
                    src={ssoUrl}
                    title={notificationSummary ? 'DoseSpot Notifications' : 'DoseSpot e-Prescribe'}
                    className="border rounded-lg"
                    style={{
                      width: `${100 / IFRAME_SCALE}%`,
                      height: `${100 / IFRAME_SCALE}%`,
                      transform: `scale(${IFRAME_SCALE})`,
                      transformOrigin: 'top left',
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
